import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { KeyRound, Loader2, Save, UserRound } from 'lucide-react';
import api from '@/lib/api';
import { getAuthStore, useAuthStore } from '@/store/auth';

interface Profile {
  id: string;
  email: string;
  role: string;
  tenantId: string;
  firstName: string;
  lastName: string;
}

const ROLE_LABELS: Record<string, string> = {
  owner: 'Proprietário',
  admin: 'Administrador',
  editor: 'Editor',
  viewer: 'Leitor',
};

const inputClass =
  'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-slate-700';

const labelClass =
  'mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400';

const ProfilePage: React.FC = () => {
  const { t } = useTranslation('common');
  const tt = (key: string, fallback: string) =>
    t(key, { defaultValue: fallback });
  const user = useAuthStore((s) => s.user);

  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    api
      .get('/auth/profile')
      .then((r) => {
        const p = r.data as Profile;
        setProfile(p);
        setFirstName(p.firstName ?? '');
        setLastName(p.lastName ?? '');
      })
      .catch(() =>
        toast.error(
          tt('profile.toasts.loadError', 'Não foi possível carregar o perfil'),
        ),
      )
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;
    if (newPassword || confirmPassword) {
      if (newPassword !== confirmPassword) {
        toast.error(
          tt(
            'profile.toasts.passwordMismatch',
            'A nova senha e a confirmação não coincidem',
          ),
        );
        return;
      }
      if (newPassword.length < 8) {
        toast.error(
          tt('profile.toasts.passwordTooShort', 'A nova senha deve ter ao menos 8 caracteres'),
        );
        return;
      }
    }
    try {
      setSaving(true);
      const body: Record<string, string> = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      };
      if (newPassword) {
        body.currentPassword = currentPassword;
        body.newPassword = newPassword;
      }
      const r = await api.patch('/users/me', body);
      getAuthStore().setUser({ ...user, ...(r.data?.user ?? {}) });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success(tt('profile.toasts.saved', 'Perfil atualizado'));
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: unknown } } })
        ?.response?.data?.message;
      toast.error(
        typeof msg === 'string'
          ? msg
          : tt('profile.toasts.saveError', 'Erro ao salvar o perfil'),
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900">
          <UserRound className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            {tt('profile.title', 'Meu perfil')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {tt('profile.subtitle', 'Gerencie seus dados de acesso e senha')}
          </p>
        </div>
      </div>

      <form
        onSubmit={handleSave}
        className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900"
      >
        <section className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={labelClass} htmlFor="firstName">
              {tt('profile.fields.firstName', 'Nome')}
            </label>
            <input
              id="firstName"
              className={inputClass}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="lastName">
              {tt('profile.fields.lastName', 'Sobrenome')}
            </label>
            <input
              id="lastName"
              className={inputClass}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
            />
          </div>
          <div>
            <label className={labelClass}>{tt('profile.fields.email', 'E-mail')}</label>
            <input className={`${inputClass} opacity-60`} value={profile?.email ?? ''} disabled />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>{tt('profile.fields.role', 'Função')}</label>
              <input
                className={`${inputClass} opacity-60`}
                value={ROLE_LABELS[profile?.role ?? ''] ?? profile?.role ?? ''}
                disabled
              />
            </div>
            <div>
              <label className={labelClass}>{tt('profile.fields.tenant', 'Tenant')}</label>
              <input
                className={`${inputClass} opacity-60`}
                value={profile?.tenantId ?? ''}
                disabled
              />
            </div>
          </div>
        </section>

        <section className="border-t border-slate-200 pt-6 dark:border-slate-800">
          <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
            <KeyRound className="h-4 w-4 text-slate-400" />
            {tt('profile.passwordSection', 'Alterar senha')}
          </h2>
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className={labelClass} htmlFor="currentPassword">
                {tt('profile.fields.currentPassword', 'Senha atual')}
              </label>
              <input
                id="currentPassword"
                type="password"
                className={inputClass}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="newPassword">
                {tt('profile.fields.newPassword', 'Nova senha')}
              </label>
              <input
                id="newPassword"
                type="password"
                className={inputClass}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="confirmPassword">
                {tt('profile.fields.confirmPassword', 'Confirmar nova senha')}
              </label>
              <input
                id="confirmPassword"
                type="password"
                className={inputClass}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>
          </div>
          <p className="mt-3 text-xs text-slate-400">
            {tt(
              'profile.passwordHint',
              'Deixe os campos de senha vazios para manter a senha atual.',
            )}
          </p>
        </section>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800 disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {tt('profile.actions.save', 'Salvar alterações')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProfilePage;
